/*
 * The drifting stream of app icons: <once-app-stream>.
 *
 * Used by the home page's stream (sections/once-app-stream.liquid) and the
 * integrations card in the app bento (blocks/_once-app-bento-integrations.liquid).
 * It lives here rather than in a {% javascript %} tag because Shopify only
 * includes a section's own script on pages where that section appears.
 */

class OnceAppStream extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.once-st__track');
    this.originals = [...this.track.querySelectorAll('[data-st-icon]')];
    if (!this.originals.length) return;

    this.speed = parseFloat(this.dataset.speed) || 30;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.time = 0;

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this);

    // Only animate while the stream is on screen.
    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      entry.isIntersecting ? this.play() : this.pause();
    });
    this.visibilityObserver.observe(this);

    // The background text comes into focus as the stream scrolls up the
    // screen, and blurs again on the way back. Listening in the capture
    // phase catches scrolling on .page-wrapper as well as the window.
    this.backdrop = this.querySelector('.once-st__backdrop');
    if (this.backdrop && !this.reduceMotion) {
      this.onScroll = () => {
        if (this.revealFrame) return;
        this.revealFrame = requestAnimationFrame(() => {
          this.revealFrame = null;
          this.updateReveal();
        });
      };
      document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      window.addEventListener('resize', this.onScroll);
      this.updateReveal();
    }
  }

  // 0 while the stream's top is at the bottom of the screen, 1 once its
  // middle is a little above the middle of the screen.
  updateReveal() {
    const box = this.track.getBoundingClientRect();
    const screen = window.innerHeight;
    const start = screen;
    const end = screen * 0.45;
    const middle = box.top + box.height / 2;
    const progress = Math.min(1, Math.max(0, (start - middle) / (start - end)));
    this.backdrop.style.setProperty('--once-st-reveal', progress.toFixed(3));
  }

  disconnectedCallback() {
    this.pause();
    this.resizeObserver?.disconnect();
    this.visibilityObserver?.disconnect();
    if (this.onScroll) {
      document.removeEventListener('scroll', this.onScroll, { capture: true });
      window.removeEventListener('resize', this.onScroll);
      cancelAnimationFrame(this.revealFrame);
    }
  }

  // Measure the stream and make sure there are enough icons to fill it,
  // repeating the set with copies (hidden from screen readers) if needed.
  layout() {
    const style = getComputedStyle(this);
    this.width = this.track.clientWidth;
    this.height = this.track.clientHeight;
    this.iconSize = parseFloat(style.getPropertyValue('--once-st-icon-size')) || 56;
    // The gap is set for desktop icons; smaller icons on phones get a
    // proportionally smaller gap.
    const desktopIcon = parseFloat(style.getPropertyValue('--once-st-icon')) || this.iconSize;
    const gap = (parseFloat(style.getPropertyValue('--once-st-gap')) || 0) * (this.iconSize / desktopIcon);
    this.spacing = this.iconSize + gap;
    // Phones have their own wave height (0 by default: a straight line).
    // Judged by the screen, not the stream, which can be narrow on a big
    // screen (e.g. in a half width bento card).
    const wave = parseFloat(window.innerWidth < 750 ? this.dataset.waveMobile : this.dataset.wave) || 0;
    this.amplitude = Math.min(wave, (this.height - this.iconSize) / 2);

    const needed = Math.ceil((this.width + this.spacing * 2) / this.spacing);
    const sets = Math.max(1, Math.ceil(needed / this.originals.length));
    this.track.querySelectorAll('[data-st-copy]').forEach((copy) => copy.remove());
    for (let set = 1; set < sets; set++) {
      this.originals.forEach((icon) => {
        const copy = icon.cloneNode(true);
        copy.setAttribute('data-st-copy', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('data-shopify-editor-block');
        this.track.append(copy);
      });
    }
    this.icons = [...this.track.querySelectorAll('[data-st-icon]')];
    this.loop = this.icons.length * this.spacing;
    this.render();
  }

  play() {
    if (this.reduceMotion || this.frame) return;
    let last = performance.now();
    const tick = (now) => {
      // Cap the step so returning to a background tab doesn't jump.
      this.time += Math.min(now - last, 100) / 1000;
      last = now;
      this.render();
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  pause() {
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  // Each icon sits a fixed distance behind the one in front, moving right
  // along a wave: rising from the left, cresting a third of the way across,
  // dipping low towards the right and rising again at the far edge.
  render() {
    if (!this.icons || !this.width) return;
    const offset = this.time * this.speed;
    const middle = this.height / 2;
    this.icons.forEach((icon, i) => {
      const x = ((i * this.spacing + offset) % this.loop) - this.spacing;
      const along = x / this.width;
      const y = middle - this.amplitude * Math.cos((2 * Math.PI * (along - 0.3)) / 0.85);
      icon.style.transform = `translate3d(${x.toFixed(1)}px, ${(y - this.iconSize / 2).toFixed(1)}px, 0)`;
    });
  }
}

if (!customElements.get('once-app-stream')) {
  customElements.define('once-app-stream', OnceAppStream);
}
